import React, { useState, useEffect, Fragment } from "react";
import { AlertUnsavedData, Modal } from "components/notifications";
import PropTypes from "prop-types";
import { useDirtyData } from 'hooks/useDirtyData';
import { FormTextInput } from 'components/form-fields';
import { useValidation } from "hooks/useValidation";
import { Button, ButtonSave } from "components/buttons";

export function AssetTypeSubTypesModal(
    {
        showModal,
        modalTitle,
        assetTypeId,
        isUpdate = false,
        loading = true,
        onSave,
        onCloseModal,
        assetSubTypeDetails,
        assetTypeSubTypeNames = []
    }
) {
    const assetSubTypeModel = {
        id: "",
        name: ""
    };
    
    const [originalAssetSubType, setOriginalAssetSubType] = useState(assetSubTypeModel);
    const [assetSubType, setAssetSubType] = useState(originalAssetSubType);
    const isAssetSubTypeDirty = useDirtyData(originalAssetSubType, assetSubType);
    const [isAssetSubTypeValid, assetSubTypeErrors, validateAssetSubType] = useValidation();
    const [blockModalClose, setBlockModalClose] = useState(false);
    
    useEffect(loadDetails, [assetSubTypeDetails]);

    function loadDetails() {
        if (!showModal || !assetSubTypeDetails) {
            return;
        }

        const mappedCentreDetails = {
            id: assetSubTypeDetails.id,
            name: assetSubTypeDetails.name
        };

        setOriginalAssetSubType(mappedCentreDetails); 
        setAssetSubType(mappedCentreDetails);
    }

    function onAssetSubTypeNameChange(passedValue) {
        setAssetSubType({
            ...assetSubType,
            name: passedValue
        });
    }

    useEffect(validate, [
        assetSubType, 
        isAssetSubTypeDirty, 
        isAssetSubTypeValid,
        loading
    ]);

    function validate() {
        if (!showModal || loading) {
            return; 
        }

        let rules = [
            { fieldName: "name", required: true, maxLength: 100, notInCollection: { collection: assetTypeSubTypeNames, originalValue: originalAssetSubType.name } }
        ];

        validateAssetSubType(rules, assetSubType);
    }

    function onSaveClick() {
        if (!isUpdate && 
            (
                !isAssetSubTypeValid || 
                !isAssetSubTypeDirty
            )
        ) {
            return;
        }

        const subType = {
            id: assetSubType.id,
            name: assetSubType.name,
            canDelete: true,
            assetTypeId: assetTypeId
        };
    
        resetModal();

        if (onSave) {
            onSave(subType, isUpdate ? "update" : "add", isUpdate ? "updated" : "added");
        }
    }

    function onCloseClick(forceClose = false) {
        if (isAssetSubTypeDirty && !forceClose) {
            setBlockModalClose(true);
            return;
        }

        resetModal();

        if (onCloseModal) {
            onCloseModal();
        }
    }

    function resetModal() {
        setBlockModalClose(false);
        setOriginalAssetSubType(assetSubTypeModel); 
        setAssetSubType(assetSubTypeModel);
    }
    
    return (
        <Fragment>
            <Modal 
                isOpen={showModal}
                onClose={onCloseClick}
                clickOutsideClose={true}
                title={modalTitle}
                isCloseButtonShown={false}
                updateStateOnClose={false}
            >
                <FormTextInput
                    id="input-sub-type-name"
                    disabled={loading}
                    loading={loading}
                    value={assetSubType.name}
                    headingText="Subtype Name"
                    placeholder="Please type the subtype name here"
                    onChange={(event) => onAssetSubTypeNameChange(event.target.value)}
                    dangerHelperText={assetSubTypeErrors.name}
                    fill={true}
                />
                <div className="bp6-dialog-footer">
                    <div className="bp6-dialog-footer-actions">
                        <Button
                            text="Cancel"
                            intent="none"
                            onClick={onCloseClick}
                        />
                        <ButtonSave
                            id="add-asset-type-sub-type-button"
                            onClick={onSaveClick}
                            simpleDisabled={!isAssetSubTypeValid || loading}
                            text={'Save'}
                        />
                    </div>
                </div>
            </Modal>

            <AlertUnsavedData
                isDirty={isAssetSubTypeDirty}
                isDirtySamePage={blockModalClose}
                onCancelLeave={() => setBlockModalClose(false)}
                onConfirmLeave={() => onCloseClick(true)}
            />
        </Fragment>
    );
}

AssetTypeSubTypesModal.propTypes = {
    showModal: PropTypes.bool.isRequired,
    modalTitle: PropTypes.string.isRequired,
    assetTypeId: PropTypes.number,
    isUpdate: PropTypes.bool,
    loading: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    onCloseModal: PropTypes.func.isRequired,
    assetSubTypeDetails: PropTypes.object.isRequired,
    assetTypeSubTypeNames: PropTypes.array.isRequired
};